import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Shield, AlertTriangle, CheckCircle2, ArrowDown, ArrowUp, Activity, Waves, Play, Loader2, ChevronDown } from "lucide-react";
import { Gauge } from "../components/Gauge";
import { RepMetric } from "../components/RepMetric";
import { PhaseSteps } from "../components/PhaseSteps";
import { api, fmt } from "../lib/api";
import { measurePing, measureDownload, measureUpload, deviceInfo, DURATION } from "../lib/speedtest";
import { CountdownRing } from "../components/CountdownRing";
import { resolveServers, probe, serverMeta } from "../lib/servers";
import { captureLocation } from "../lib/location";
import { RepHistory } from "../components/RepHistory";
import { RewardsCard } from "../components/RewardsCard";
import { toProvince, PHONE_RE, formatPhone } from "../lib/province";
import { IdentityBar } from "../components/IdentityBar";
import { InstallBanner } from "../components/InstallBanner";

let autoStarted = false;
const fmtData = (mb) => (mb >= 1000 ? `${(mb / 1000).toFixed(mb % 1000 ? 1 : 0)}GB` : `${mb}MB`);
const speedIssues = (t) => (t?.issues || []).filter((i) => /download|upload/i.test(i));

export default function RepTest() {
  const savedPhone = localStorage.getItem("rep_name") || "";
  const [name, setName] = useState(PHONE_RE.test(savedPhone) ? savedPhone : "");
  const [team, setTeam] = useState(localStorage.getItem("rep_team") || "");
  const [editing, setEditing] = useState(!PHONE_RE.test(savedPhone));
  const [detecting, setDetecting] = useState(true);
  const [notes, setNotes] = useState("");
  const [phase, setPhase] = useState("");
  const [live, setLive] = useState(0);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [partial, setPartial] = useState({});
  const [step, setStep] = useState(null);

  const loadHistory = (p = name) => {
    if (!PHONE_RE.test(p)) return;
    api.myTests(p).then(setHistory).catch(() => {});
    api.wallet(p).then(setWallet).catch(() => {});
  };

  useEffect(() => {
    captureLocation().then((l) => {
      const p = toProvince(l.location?.region);
      const t = p || l.location?.region;
      if (t) { setTeam(t); localStorage.setItem("rep_team", t); }
    }).finally(() => setDetecting(false));
  }, []);

  useEffect(() => {
    if (autoStarted || !new URLSearchParams(window.location.search).has("autostart")) return;
    autoStarted = true;
    window.history.replaceState(null, "", "/");
    if (editing) {
      toast.info("Enter your phone number once — future launches will auto-test");
      return;
    }
    run();
  }, []); // eslint-disable-line
  useEffect(() => { loadHistory(); }, []); // eslint-disable-line

  const run = async () => {
    if (!PHONE_RE.test(name)) return toast.error("Enter your phone number as xxx-xxx-xxxx");
    localStorage.setItem("rep_name", name);
    setEditing(false);
    setRunning(true); setResult(null); setLive(0); setPartial({});
    try {
      const t0 = performance.now();
      setPhase("Locating");
      const locPromise = captureLocation();
      const cfg = await api.settings().catch(() => ({}));
      const failed = [];
      let m = null;
      for (const srv of resolveServers(cfg)) {
        try {
          setPhase("Ping"); await probe(srv.target);
          setPartial({});
          const { ping, jitter } = await measurePing(srv.target);
          setPartial({ ping, jitter });
          setPhase("Download"); setStep({ start: Date.now(), duration: DURATION });
          const dl = await measureDownload(srv.target, setLive);
          setPartial((p) => ({ ...p, dl: dl.mbps }));
          setLive(0); setPhase("Upload"); setStep({ start: Date.now(), duration: DURATION });
          const ul = await measureUpload(srv.target, setLive);
          setPartial((p) => ({ ...p, ul: ul.mbps }));
          m = { ping, jitter, dl, ul, server: await serverMeta(srv) };
          break;
        } catch {
          failed.push(srv.id); setLive(0);
        }
      }
      if (!m) throw new Error("All test servers unreachable");
      const { ping, jitter, dl, ul, server } = m;
      setPhase("Saving"); setStep(null);
      const loc = await locPromise;
      const province = toProvince(loc.location?.region) || loc.location?.region || team || "Unknown";
      if (province !== team) { setTeam(province); localStorage.setItem("rep_team", province); }
      const saved = await api.saveTest({
        rep_name: name, team: province, notes: notes || null,
        download_mbps: dl.mbps, upload_mbps: ul.mbps, ping_ms: ping, jitter_ms: jitter,
        bytes_downloaded: dl.bytes, bytes_uploaded: ul.bytes,
        duration_s: (performance.now() - t0) / 1000,
        ...loc, ...deviceInfo(), ...server, failed_servers: failed,
      });
      setResult(saved); setLive(saved.download_mbps); setPhase("Complete");
      speedIssues(saved).length ? toast.warning(`${speedIssues(saved).length} speed issue(s) detected`) : toast.success("Test logged — connection healthy");
      if (saved.reward_mb) toast.success(`+${saved.reward_mb}MB Megatel data earned!`);
      else if (saved.location?.lat == null) toast.info("Turn on location to earn your weekly 500MB reward");
      setNotes("");
      loadHistory();
    } catch (e) {
      toast.error("Test failed — check your connection"); setPhase("Failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="rep min-h-screen max-w-xl mx-auto flex flex-col relative z-10 pb-10">
      <header className="flex items-center justify-between gap-2 px-5 pt-5 pb-3">
        <div className="flex items-center gap-2.5">
          <img src="/logo-64.png" alt="CellCheckPoint" className="w-8 h-8 rounded-xl" />
          <span className="font-rounded font-semibold text-lg tracking-tight" data-testid="brand-logo">Cell<span className="text-[#3B9BFF]">CheckPoint</span></span>
        </div>
        <div className="flex items-center gap-2">
        <a href="https://mega-tel.co.za/" target="_blank" rel="noopener noreferrer" data-testid="sponsor-banner"
          className="flex items-center gap-1.5 whitespace-nowrap shrink-0 rounded-full bg-white/[0.05] border border-white/[0.07] px-2 py-1.5 text-[9px] text-neutral-400 hover:bg-white/[0.09] transition-colors">
          Sponsored by
          <img src="/megatel-wordmark.png" alt="Megatel" className="h-2.5 w-auto" data-testid="sponsor-logo" />
        </a>
        <Link to="/admin" data-testid="admin-link" aria-label="Admin" title="Admin" className="w-8 h-8 shrink-0 rounded-full bg-white/[0.06] text-neutral-300 flex items-center justify-center hover:bg-white/10 hover:text-white transition-colors">
          <Shield className="w-4 h-4" />
        </Link>
        </div>
      </header>
      {wallet && !editing && (
        <button data-testid="header-balance" onClick={() => document.querySelector("[data-testid=rewards-card]")?.scrollIntoView({ behavior: "smooth", block: "center" })}
          className="rewards-card mx-5 mb-1 rounded-full p-[1px]" aria-label="Megatel rewards balance">
          <span className="flex items-center justify-center gap-2 rounded-full bg-[#0d1a14] py-2 text-xs text-neutral-300">
            <img src="/megatel-wordmark.png" alt="Megatel" className="h-4 w-auto" />
            <span>rewards balance</span>
            <span className="font-rounded font-semibold text-sm text-white tabular-nums">{fmtData(wallet.balance_mb)}</span>
            <ChevronDown className="w-3.5 h-3.5 text-[#32D74B]" />
          </span>
        </button>
      )}
      <InstallBanner />

      <section className="pt-6 pb-2 fade-up">
        <Gauge value={live} phase={phase} running={running} />
        <div className="h-9 -mt-12 mb-3 flex justify-center relative z-10">{running && step && <CountdownRing key={step.start} {...step} />}</div>
        <PhaseSteps phase={phase} />
      </section>

      <section className="grid grid-cols-2 gap-3 mx-5 mt-6">
        <RepMetric testId="result-download" icon={ArrowDown} color="#3B9BFF" label="Download" unit="Mbps" value={fmt(result?.download_mbps ?? partial.dl)} fresh={!result && partial.dl != null} bad={result?.issues.some((i) => i.includes("download"))} />
        <RepMetric testId="result-upload" icon={ArrowUp} color="#32D74B" label="Upload" unit="Mbps" value={fmt(result?.upload_mbps ?? partial.ul)} fresh={!result && partial.ul != null} bad={result?.issues.some((i) => i.includes("upload"))} />
        <RepMetric testId="result-ping" icon={Activity} color="#FF9F0A" label="Ping" unit="ms" value={fmt(result?.ping_ms ?? partial.ping, 0)} fresh={!result && partial.ping != null} />
        <RepMetric testId="result-jitter" icon={Waves} color="#BF5AF2" label="Jitter" unit="ms" value={fmt(result?.jitter_ms ?? partial.jitter, 0)} fresh={!result && partial.jitter != null} />
      </section>

      {result && (
        <section className="mx-5 mt-3 p-4 rounded-2xl bg-white/[0.04] border border-white/[0.06] text-xs space-y-2 fade-up" data-testid="result-summary">
          <div className="flex items-center gap-2 text-neutral-200">
            <MapPin className="w-3.5 h-3.5 text-[#3B9BFF]" />
            <span data-testid="result-location">
              {[result.location.city, result.location.region, result.location.country].filter(Boolean).join(", ") || "Location unavailable"}
              <span className="ml-1 rounded-full bg-white/[0.06] px-2 py-0.5 text-[10px] text-neutral-400">{result.location.source}</span>
            </span>
          </div>
          <div className="text-neutral-500">ISP: {result.isp || "—"} · IP: {result.ip || "—"} · {result.effective_type || ""}</div>
          {speedIssues(result).length ? (
            <ul className="text-[#FF6B61] space-y-1" data-testid="result-issues">
              {speedIssues(result).map((i) => <li key={i} className="flex gap-2"><AlertTriangle className="w-3 h-3 mt-0.5" />{i}</li>)}
            </ul>
          ) : (
            <div className="text-[#32D74B] flex gap-2" data-testid="result-healthy"><CheckCircle2 className="w-3 h-3 mt-0.5" />Download & upload within thresholds</div>
          )}
        </section>
      )}

      <section className="mx-5 mt-6 space-y-3">
        {editing ? (
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="rep-name-input" className="field" type="tel" inputMode="numeric" placeholder="Phone xxx-xxx-xxxx" value={name} onChange={(e) => setName(formatPhone(e.target.value))} disabled={running} />
            <div data-testid="rep-province-display" className="field flex items-center gap-2 text-neutral-300 select-none" title="Auto-detected from your location">
              <MapPin className="w-3.5 h-3.5 text-[#3B9BFF] shrink-0" />
              <span className="truncate">{team || (detecting ? "Detecting province…" : "Detected on scan")}</span>
            </div>
          </div>
        ) : (
          <IdentityBar name={name} team={team} onEdit={() => setEditing(true)} disabled={running} />
        )}
        <input data-testid="rep-notes-input" className="field w-full" placeholder="Notes (optional) — e.g. client office, 3rd floor" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={running} />
      </section>
      <div className="sticky bottom-0 z-20 px-5 pt-4 pb-5 mt-2 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/90 to-transparent">
        <button data-testid="run-test-button" onClick={run} disabled={running} className="btn-go w-full py-4 rounded-full font-rounded font-semibold text-lg flex items-center justify-center gap-2">
          {running ? <><Loader2 className="w-5 h-5 animate-spin" /> Testing · {phase}</> : <><Play className="w-5 h-5 fill-current" /> {result ? "Run again" : "Start test"}</>}
        </button>
      </div>

      {!editing && <RewardsCard phone={name} wallet={wallet} onChange={setWallet} />}
      <RepHistory items={history} />
    </div>
  );
}
